import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { assetsApi, pickersApi, protocolsApi } from "../api/assetRegistry";

const ASSETS_QK = "assets";
const PROTOCOLS_QK = "handover-protocols";

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: [ASSETS_QK] });
  qc.invalidateQueries({ queryKey: [PROTOCOLS_QK] });
}

// ── Assets ──────────────────────────────────────────────────────────────────

export function useAssets(params?: Record<string, string>) {
  return useQuery({
    queryKey: [ASSETS_QK, params],
    queryFn: () => assetsApi.list(params),
    staleTime: 0,
  });
}

export function useAssignableAssets(enabled = true) {
  return useQuery({
    queryKey: [ASSETS_QK, "assignable"],
    queryFn: () => assetsApi.assignable(),
    enabled,
    staleTime: 0,
  });
}

export function useCreateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: assetsApi.create,
    onSuccess: () => qc.invalidateQueries({ queryKey: [ASSETS_QK] }),
  });
}

export function useUpdateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof assetsApi.update>[1] }) =>
      assetsApi.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [ASSETS_QK] }),
  });
}

export function useDeleteAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: assetsApi.delete,
    onSuccess: () => qc.invalidateQueries({ queryKey: [ASSETS_QK] }),
  });
}

export function useAssignAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { employee: string; handover_date?: string } }) =>
      assetsApi.assign(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [ASSETS_QK] }),
  });
}

export function useUnassignAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => assetsApi.unassign(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [ASSETS_QK] }),
  });
}

export function useSetAssetVisibility() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, visibility }: { id: string; visibility: string }) =>
      assetsApi.setVisibility(id, visibility),
    onSuccess: () => qc.invalidateQueries({ queryKey: [ASSETS_QK] }),
  });
}

export function useTransferAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, branch }: { id: string; branch: string }) =>
      assetsApi.transfer(id, branch),
    onSuccess: () => qc.invalidateQueries({ queryKey: [ASSETS_QK] }),
  });
}

// ── Pickers (users / branches) ────────────────────────────────────────────────

export function useInternalUsersList(enabled = true) {
  return useQuery({
    queryKey: ["asset-registry-users"],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: () =>
      pickersApi.users().then((users) =>
        users
          .map((u) => ({ id: u.id, name: `${u.first_name} ${u.last_name}`.trim() }))
          .sort((a, b) => a.name.localeCompare(b.name, "pl")),
      ),
  });
}

export function useBranchesList(enabled = true) {
  return useQuery({
    queryKey: ["asset-registry-branches"],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: () =>
      pickersApi.branches().then((branches) =>
        [...branches].sort((a, b) => a.name.localeCompare(b.name, "pl")),
      ),
  });
}

// ── Handover protocols ────────────────────────────────────────────────────────

export function useHandoverProtocols(params?: Record<string, string>, enabled = true) {
  return useQuery({
    queryKey: [PROTOCOLS_QK, params],
    queryFn: () => protocolsApi.list(params),
    enabled,
    staleTime: 0,
  });
}

export function useCreateHandoverProtocol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: protocolsApi.create,
    onSuccess: () => invalidateAll(qc),
  });
}

export function useApproveHandoverProtocol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      protocolsApi.approve(id, comment),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useRejectHandoverProtocol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment: string }) =>
      protocolsApi.reject(id, comment),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useHandoverPendingCount(enabled = true) {
  return useQuery({
    queryKey: ["handover-pending-count"],
    queryFn: () => protocolsApi.pendingCount(),
    enabled,
    staleTime: 30_000,
  });
}
